import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';

type PushState = 'unsupported' | 'unknown' | 'denied' | 'unsubscribed' | 'subscribed';

// Le type de retour est explicitement adossé à un ArrayBuffer : `new Uint8Array(n)`
// est inféré `Uint8Array<ArrayBufferLike>` depuis TS 5.7, et `pushManager.subscribe`
// n'accepte qu'un `BufferSource` sur ArrayBuffer — un SharedArrayBuffer ne passe pas.
function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function isSupported() {
  return typeof window !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window;
}

export function usePushNotifications() {
  const [state, setState] = useState<PushState>(() => {
    if (!isSupported()) return 'unsupported';
    if (Notification.permission === 'denied') return 'denied';
    return 'unknown';
  });
  const [isWorking, setIsWorking] = useState(false);

  const refreshState = useCallback(async () => {
    if (!isSupported()) {
      setState('unsupported');
      return;
    }
    if (Notification.permission === 'denied') {
      setState('denied');
      return;
    }
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setState(sub ? 'subscribed' : 'unsubscribed');
    } catch {
      setState('unknown');
    }
  }, []);

  useEffect(() => { refreshState(); }, [refreshState]);

  const enable = useCallback(async () => {
    if (!isSupported()) return;
    setIsWorking(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'unknown');
        return;
      }
      const cfg = await api.push.config();
      if (!cfg.enabled || !cfg.publicKey) throw new Error('push_not_configured');

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(cfg.publicKey),
        });
      }
      await api.push.subscribe(sub.toJSON() as PushSubscriptionJSON);
      setState('subscribed');
    } finally {
      setIsWorking(false);
    }
  }, []);

  const disable = useCallback(async () => {
    if (!isSupported()) return;
    setIsWorking(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await api.push.unsubscribe(sub.endpoint);
        await sub.unsubscribe();
      }
      setState('unsubscribed');
    } finally {
      setIsWorking(false);
    }
  }, []);

  const sendTest = useCallback(async () => {
    return api.push.test();
  }, []);

  return { state, isWorking, enable, disable, sendTest, refreshState };
}
